import type { Rgb } from './colorScale';
import type { CameraBasis } from '../geometry/projection';

export type Vector3 = readonly [x: number, y: number, z: number];

/** Per-channel light colour and shading terms, 0 to 1. */
export type Rgb01 = readonly [r: number, g: number, b: number];

/**
 * Whether a light is fixed to the plot or to the camera.
 *
 * A `world` light stays put as the plot turns, which is what makes relief legible - the slope
 * facing the light is the same slope from every angle, so brightness means orientation. A `view`
 * light travels with the eye, so it always lifts whatever is facing the viewer; its direction is
 * given as coefficients on the camera's own axes, `[right, up, forward]`, with `forward` pointing
 * from the plot toward the eye. Both have a place: the world light carries the terrain's shape and
 * the view light keeps the near face from falling into shadow at every angle.
 */
export type LightSpace = 'world' | 'view';

export type Light = {
  /** Direction the light arrives from. Normalised, and interpreted per {@link LightSpace}. */
  direction: Vector3;
  space: LightSpace;
  color: Rgb01;
  diffuse: number;
  specular: number;
};

export type Fog = {
  color: Rgb01;
  /** How much of the far edge is washed toward the fog colour. 0 disables it. */
  strength: number;
};

export type LightingModel = {
  ambient: number;
  shininess: number;
  /**
   * One or two lights, and no more.
   *
   * A tuple rather than an array because the GPU path packs a fixed-size uniform struct: a third
   * entry was honoured by the software renderer and silently dropped by the shader, so the same
   * rig drew differently depending on whether the machine had an adapter. Fewer than two is
   * handled - the packer pads the unused slot with zero diffuse - and more is now a type error at
   * the call site rather than a picture that depends on the driver.
   */
  lights: readonly [Light] | readonly [Light, Light];
  fog: Fog;
};

const normalise = ([x, y, z]: Vector3): Vector3 => {
  const length = Math.hypot(x, y, z) || 1;
  return [x / length, y / length, z / length];
};

/**
 * A two-light rig, plus a faint fog.
 *
 * A single white light from above renders every slope of equal aspect identically and looks
 * inert - the surface reads as a shaded relief map rather than an object. Splitting it gives the
 * two channels separate jobs: a dim blue fill from overhead, which is where sky light comes from
 * and which cools the flats; and a white key just behind the viewer's shoulder, offset up and
 * left, which picks out the near faces and puts the specular highlight where the eye already is.
 *
 * `ambient` plus the diffuse terms sum to 1 on purpose. Lighting and the colour ramp compete for
 * the same channel, and if the lights can drive a facet far from its own colour then height is no
 * longer readable from colour and the legend stops being true. Keeping the total at unity means a
 * facet turned toward the light is at its ramp colour and one turned away is dimmer, rather than
 * the whole surface floating somewhere else entirely.
 */
/**
 * Sky fill. Overhead and cool, so flat ground reads as lit from the sky rather than from nowhere.
 * Deliberately weak - it is doing atmosphere, not shape.
 */
export const SKY_FILL_LIGHT: Light = {
  direction: normalise([-0.15, -0.1, 1]),
  space: 'world',
  color: [0.55, 0.72, 1],
  diffuse: 0.12,
  specular: 0.02,
};

/**
 * Key light, over the viewer's left shoulder. Given in camera axes, so it follows the orbit and
 * the near face never falls dark - which a fixed light cannot promise at every angle.
 */
export const KEY_LIGHT: Light = {
  direction: normalise([-0.3, 0.35, 0.88]),
  space: 'view',
  color: [1, 1, 1],
  diffuse: 0.26,
  specular: 0.14,
};

export const DEFAULT_LIGHTING: LightingModel = {
  ambient: 0.62,
  shininess: 28,
  lights: [SKY_FILL_LIGHT, KEY_LIGHT],
  fog: {
    /* Very faint, and light blue: aerial perspective, the same cue that makes distant hills pale.
       Kept low because it trades colour accuracy for depth - a far peak washed toward the fog
       reads as a slightly lower value than an equal near one. */
    color: [0.86, 0.92, 0.99],
    strength: 0.1,
  },
};

const dot = (a: Vector3, b: Vector3): number =>
  a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/**
 * Resolves a light's direction into world space.
 *
 * A view-space direction is a combination of the camera's own axes, so it has to be rebuilt
 * whenever the camera moves - which is why this takes the basis rather than caching.
 */
export const toWorldLightDirection = (
  { direction, space }: Light,
  basis: CameraBasis
): Vector3 => {
  if (space === 'world') return direction;

  const [alongRight, alongUp, alongForward] = direction;

  return normalise([
    basis.right[0] * alongRight +
      basis.up[0] * alongUp +
      basis.forward[0] * alongForward,
    basis.right[1] * alongRight +
      basis.up[1] * alongUp +
      basis.forward[1] * alongForward,
    basis.right[2] * alongRight +
      basis.up[2] * alongUp +
      basis.forward[2] * alongForward,
  ]);
};

export type ShadingTerms = {
  /** Per-channel multiplier on the facet's own colour. */
  intensity: Rgb01;
  /** Per-channel additive highlight. */
  highlight: Rgb01;
};

/**
 * Blinn-Phong across every light, accumulated per channel.
 *
 * Per channel rather than as a scalar because the lights are coloured: a blue fill and a white key
 * cannot be summed into one brightness without losing the tint that distinguishes them. Blinn's
 * halfway vector rather than a reflected ray - one normalise instead of a reflection, and the
 * difference is invisible at these mesh sizes.
 */
/**
 * A light with its camera-relative parts already worked out.
 *
 * The world direction and the halfway vector are functions of the light and the camera basis, and
 * of nothing that varies across a surface - so resolving them per pixel, as `getShadingTerms`
 * does, repeats nine multiplies, a `Math.hypot` and three divides for every covered pixel of every
 * frame. {@link resolveLights} does it once.
 */
export type ResolvedLight = {
  direction: Vector3;
  halfway: Vector3;
  diffuse: number;
  specular: number;
  color: Vector3;
};

export const resolveLights = (
  lighting: LightingModel,
  basis: CameraBasis
): ResolvedLight[] =>
  lighting.lights.map(light => {
    const direction = toWorldLightDirection(light, basis);
    return {
      direction,
      halfway: normalise([
        direction[0] + basis.forward[0],
        direction[1] + basis.forward[1],
        direction[2] + basis.forward[2],
      ]),
      diffuse: light.diffuse,
      specular: light.specular,
      color: light.color,
    };
  });

/**
 * The sRGB transfer function, both directions, over 0-1 channels.
 *
 * Exact rather than a 2.2 power, because the linear segment near black is where the difference
 * shows: a plain power law sends the darkest few values to almost nothing, which on a shaded
 * sphere is a terminator that crushes to black instead of falling off.
 *
 * Light adds and mixes linearly and color channels are stored encoded, so any renderer that sums
 * two lights, adds a highlight or mixes toward fog has to decode first. Doing it on encoded values
 * instead is not a small error in the result: it is the wrong curve, and it shows as a specular
 * that reads as a decal stuck on the mark rather than as a gloss on it.
 */
export const srgbToLinear = (channel: number): number =>
  channel <= 0.04045
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4);

/**
 * How an encoded multiplier converts to the linear weight that means the same thing.
 *
 * The rig's weights were chosen by eye against renderers that multiplied them into encoded color,
 * so they are perceptual quantities. Read unchanged as linear radiance they describe a much
 * brighter shadow - `ambient` of 0.62 lands a turned-away facet at 0.80 encoded rather than 0.62 -
 * and a sphere with a shadow that bright has no form left.
 */
const ENCODED_TO_LINEAR_EXPONENT = 2.2;

/**
 * The same rig with its weights read as linear radiance rather than as multipliers on encoded
 * color.
 *
 * `ambient` converts through the power law, which is what holds a turned-away facet at the
 * brightness it was tuned to. The diffuse weights then take whatever is left of the rig's original
 * total, in the proportions they were authored in - *not* each through the power law, which is
 * convex and would drop the total from 1.0 to 0.41 and darken every fully-lit facet by a third.
 * That total is the invariant this cannot break: `ambient` plus the diffuse terms summing to 1 is
 * what puts a facet turned toward the light at exactly its ramp color, which is what keeps the
 * legend true.
 *
 * Specular is carried across untouched. It is a radiance already, and there is nothing in the old
 * value to preserve: adding a constant to encoded color raises a dark pixel enormously and a bright
 * one barely, which is the artifact, and the value that produced it was tuned against an addition
 * that clipped. Converted through the power law as well it came out six times weaker and the
 * spheres went matte - which loses the one cue that separates a node in front from a node behind.
 * `addHighlight` is what makes leaving it alone safe: the gloss lands in the headroom rather than
 * over the top of it.
 *
 * Applied once per rig rather than per pixel. `markShading` deliberately does not use this: it
 * composites a neutral overlay through the canvas, which blends in encoded space and is not ours
 * to change, so its weights stay the perceptual ones they were tuned as.
 */
export const toLinearLighting = (lighting: LightingModel): LightingModel => {
  const diffuseTotal = lighting.lights.reduce(
    (total, { diffuse }) => total + diffuse,
    0
  );
  const ambient = Math.pow(lighting.ambient, ENCODED_TO_LINEAR_EXPONENT);
  const remaining = Math.max(0, lighting.ambient + diffuseTotal - ambient);
  const diffuseScale = diffuseTotal > 0 ? remaining / diffuseTotal : 0;

  /* Rebuilt by arity rather than mapped, because `lights` is a one-or-two tuple and `map` answers
     an array - the type is what documents that the shaders' uniform struct holds exactly two. */
  const toLinearLight = (light: Light): Light => ({
    ...light,
    diffuse: light.diffuse * diffuseScale,
  });

  return {
    ...lighting,
    ambient,
    lights:
      lighting.lights.length === 2
        ? [toLinearLight(lighting.lights[0]), toLinearLight(lighting.lights[1])]
        : [toLinearLight(lighting.lights[0])],
    fog: {
      ...lighting.fog,
      /* Mixed in linear too, so the fog color has to arrive linear. */
      color: [
        srgbToLinear(lighting.fog.color[0]),
        srgbToLinear(lighting.fog.color[1]),
        srgbToLinear(lighting.fog.color[2]),
      ],
    },
  };
};

/**
 * The shading at one normal, from lights already resolved against the camera.
 *
 * Writes into the caller's arrays rather than allocating: at a million covered pixels a frame the
 * allocation is the cost rather than the arithmetic.
 */
export const accumulateShading = (
  normal: Vector3,
  lights: readonly ResolvedLight[],
  ambient: number,
  shininess: number,
  intensity: [number, number, number],
  highlight: [number, number, number],
  /** Bands each light's diffuse term is rounded to, for cel shading; 0 leaves it smooth. */
  celSteps: number
): void => {
  intensity[0] = ambient;
  intensity[1] = ambient;
  intensity[2] = ambient;
  highlight[0] = 0;
  highlight[1] = 0;
  highlight[2] = 0;

  for (const light of lights) {
    const lambert = toCelLambert(
      Math.max(0, dot(normal, light.direction)),
      celSteps
    );
    const specular =
      light.specular *
      Math.pow(Math.max(0, dot(normal, light.halfway)), shininess);

    for (let channel = 0; channel < 3; channel++) {
      intensity[channel] += light.diffuse * lambert * light.color[channel];
      highlight[channel] += specular * light.color[channel];
    }
  }
};

/**
 * A diffuse term rounded to the nearest of `steps` bands, the flat tones of cel shading. Unchanged
 * at 0. Mirrored by `toCelLambert` in the surface shader.
 */
export const toCelLambert = (lambert: number, steps: number): number =>
  steps > 0 ? Math.round(lambert * steps) / steps : lambert;

export const getShadingTerms = (
  normal: Vector3,
  basis: CameraBasis,
  lighting: LightingModel
): ShadingTerms => {
  const { ambient, shininess } = lighting;
  const intensity: [number, number, number] = [ambient, ambient, ambient];
  const highlight: [number, number, number] = [0, 0, 0];

  accumulateShading(
    normal,
    resolveLights(lighting, basis),
    ambient,
    shininess,
    intensity,
    highlight,
    0
  );

  return { intensity, highlight };
};

/** The ambient light reaching a face that points straight down, per channel: darker and cooler. */
export const GROUND_BOUNCE: Rgb01 = [0.5, 0.56, 0.74];

/**
 * Turns the ambient term of a back face into ground bounce, in place.
 *
 * `normalZ` is the flipped normal's, facing the eye. The ambient term is scaled per channel by a
 * blend from {@link GROUND_BOUNCE} (normal down) to unity (normal up).
 */
export const addGroundBounce = (
  normalZ: number,
  ambient: number,
  intensity: [number, number, number]
): void => {
  const up = (normalZ + 1) / 2;
  for (let channel = 0; channel < 3; channel++) {
    const bounce = GROUND_BOUNCE[channel] + (1 - GROUND_BOUNCE[channel]) * up;
    intensity[channel] += ambient * (bounce - 1);
  }
};

/** {@link getShadingTerms} for a back face, given its normal already flipped toward the eye. */
export const getUndersideShadingTerms = (
  normal: Vector3,
  basis: CameraBasis,
  lighting: LightingModel
): ShadingTerms => {
  const { intensity, highlight } = getShadingTerms(normal, basis, lighting);
  const bounced: [number, number, number] = [...intensity];
  addGroundBounce(normal[2], lighting.ambient, bounced);
  return { intensity: bounced, highlight };
};

/**
 * Shades one encoded color, in encoded space.
 *
 * Still the encoded arithmetic, and knowingly so. The surface chart's two renderers - this one
 * through `rasterizeMesh` and the WebGPU one through `SURFACE_SHADER` - are required to agree, and
 * the GPU path reads its color from a ramp *texture*, so moving the surface to linear means
 * decoding that texture as well. Migrating one of the two and not the other would make the surface
 * chart look different depending on whether the browser has an adapter, which is worse than either
 * arithmetic. The 3D network has moved and the surface has not; `srgbToLinear`, `addHighlight` and
 * `toLinearLighting` are here as the shared vocabulary for when it does.
 */
export const applyShading = (
  [r, g, b]: Rgb,
  { intensity, highlight }: ShadingTerms
): Rgb => {
  const lit = (channel: number, index: number) =>
    Math.max(
      0,
      Math.min(
        255,
        Math.round(channel * intensity[index] + highlight[index] * 255)
      )
    );

  return [lit(r, 0), lit(g, 1), lit(b, 2)];
};

/**
 * Mixes a colour toward the fog, by how near it is.
 *
 * `nearness` runs 0 at the far edge of the plot to 1 at the near edge. Applied after shading, as
 * atmosphere sits between the eye and the surface rather than being a property of the surface.
 */
export const applyFog = (
  [r, g, b]: Rgb,
  { color, strength }: Fog,
  nearness: number
): Rgb => {
  if (strength <= 0) return [r, g, b];

  const amount = strength * (1 - Math.max(0, Math.min(1, nearness)));
  const mixed = (channel: number, fogChannel: number) =>
    Math.round(channel + (fogChannel * 255 - channel) * amount);

  return [mixed(r, color[0]), mixed(g, color[1]), mixed(b, color[2])];
};

/**
 * Scales the shading between the flat ramp colour and full relief.
 *
 * Mixing the terms rather than the resulting colours, so the dial needs no second lighting pass
 * for its midpoints.
 */
export const scaleShading = (
  { intensity, highlight }: ShadingTerms,
  amount: number
): ShadingTerms => ({
  intensity: [
    1 + (intensity[0] - 1) * amount,
    1 + (intensity[1] - 1) * amount,
    1 + (intensity[2] - 1) * amount,
  ],
  highlight: [
    highlight[0] * amount,
    highlight[1] * amount,
    highlight[2] * amount,
  ],
});
